import React, { useState } from 'react';
import { Captcha, resetCaptcha } from './Captcha';
import * as e2ee from './e2ee';
import { QrLogin } from './QrLogin';
import {
  AtSign,
  Mail,
  Lock,
  LogIn,
  ArrowLeft,
  Users,
  Volume2,
  Plus,
  Search,
  Settings,
  Mic,
  Headphones,
  Shield,
  X,
  Home,
  KeyRound,
  Download,
  Sparkles,
  Hash,
  Send
} from 'lucide-react';
import { SixDigitCodeInput } from './CustomSelect';
import { useLang } from './i18n';
import { HomeLanding } from './HomeLanding';

type Props = {
  onDone: () => Promise<void>;
  error: string;
  setError: (s: string) => void;
  mailEnabled: boolean;
  captchaSiteKey?: string;
  request: (url: string, method?: string, body?: unknown) => Promise<any>;
};

export function Auth({ onDone, error, setError, mailEnabled, captchaSiteKey, request }: Props) {
  const [, , tr] = useLang();
  const reset = new URLSearchParams(location.search).get('reset');
  // The website opens on the start page; the desktop app goes straight to login.
  const [mode, setMode] = useState(reset ? 'reset' : (window as any).malvoDesktop?.isDesktop ? 'login' : 'home');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [info, setInfo] = useState<string | null>(null);
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [challenge,setChallenge]=useState(''),[factorCode,setFactorCode]=useState(''),[useRecovery,setUseRecovery]=useState(false);

  // Interactive 3D Mouse Tilt & Scroll state
  const [tilt, setTilt] = useState<{ rx: number; ry: number }>({ rx: 5, ry: -14 });
  const [scrollAngle, setScrollAngle] = useState<number>(0);

  const change = (m: string) => {
    setMode(m);setChallenge('');setFactorCode('');setUseRecovery(false);
    setError('');
    setNote('');
  };

  function handleMouseMove(e: React.MouseEvent<HTMLDivElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    const nx = ((e.clientX - rect.left) / rect.width - 0.5) * 2; // -1 to 1
    const ny = ((e.clientY - rect.top) / rect.height - 0.5) * 2; // -1 to 1
    setTilt({
      rx: Number((-ny * 9 + 4).toFixed(2)),
      ry: Number((nx * 14 - 10).toFixed(2))
    });
  }

  function handleMouseLeave() {
    setTilt({ rx: 5, ry: -14 });
  }

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const v = Object.fromEntries(new FormData(e.currentTarget)) as Record<string, string>;
    setBusy(true);
    setError('');
    try {
      if(mode==='2fa'){await request('/auth/2fa','POST',{challenge,code:factorCode});setChallenge('');setFactorCode('');await onDone();
      } else if (mode === 'forgot') {
        await request('/auth/reset-request', 'POST', { email: v.email });
        setResetEmail(v.email);
        setResetCode('');
        setMode('reset-code');
        setNote(
          tr(
            'Verification code sent! Enter the 6-digit code from your email below.',
            'Bestätigungscode wurde versendet. Gib den 6-stelligen Code aus der E-Mail ein.'
          )
        );
      } else if (mode === 'reset-code') {
        const finalCode = (resetCode || v.code || '').replace(/\s+/g, '');
        if (finalCode.length < 6) {
          throw new Error(tr('Please enter all 6 digits of the code.', 'Bitte gib alle 6 Ziffern des Codes ein.'));
        }
        await request('/auth/reset', 'POST', {
          email: resetEmail || v.email,
          code: finalCode,
          password: v.password
        });
        change('login');
        setNote(
          tr(
            'Password updated! You can now log in.',
            'Passwort erfolgreich geändert! Du kannst dich jetzt anmelden.'
          )
        );
      } else if (mode === 'reset') {
        await request('/auth/reset', 'POST', { token: reset, password: v.password });
        history.replaceState({}, '', '/');
        change('login');
        setNote(tr('Password saved. You can now log in.', 'Passwort gespeichert. Du kannst dich anmelden.'));
      } else {
        if (v.password) e2ee.rememberPassword(v.password);
        const result = await request('/auth/' + (mode === 'register' ? 'register' : 'login'), 'POST', v);
        if(result?.twoFactorRequired){setChallenge(result.challenge);setFactorCode('');setUseRecovery(false);setMode('2fa');return;}
        // Tell the verification screen when the server could not send the first mail.
        if (result?.verificationSent === false && (mode === 'register' || result?.verificationError)) { try { sessionStorage.setItem('malvo-verify-mail-failed', '1'); sessionStorage.setItem('malvo-verify-mail-error', result.verificationError || ''); } catch {} }
        await onDone();
      }
    } catch (e) {
      setError((e as Error).message);
      if (mode === 'register') resetCaptcha();
    } finally {
      setBusy(false);
    }
  }

  const renderPreviewWindow = (extraClass = '') => (
    <div
      className={'software-window ' + extraClass}
      style={
        mode === 'home'
          ? {
              transform: `rotateY(${scrollAngle + tilt.ry * 0.5}deg) rotateX(${tilt.rx}deg) translateZ(10px)`
            }
          : undefined
      }
    >
      <div className="software-rail">
        <img src="/icon.png" alt="MALVO" />
        <div className="software-rule" />
        <span className="software-server active-server">M</span>
        <span className="software-server">✦</span>
        <span className="software-server">
          <Plus size={18} />
        </span>
        <span className="software-rail-end">
          <Settings size={17} />
        </span>
      </div>
      <div className="software-sidebar">
        <div className="software-search">
          <Search size={13} />
          <span>{tr('Search channels...', 'Kanäle suchen...')}</span>
        </div>
        <div className="software-side-link">
          <Hash size={14} />
          <span>rules</span>
        </div>
        <div className="software-side-link chosen">
          <Hash size={14} />
          <span>general</span>
        </div>
        <div className="software-side-link">
          <Volume2 size={14} />
          <span>Lounge</span>
        </div>
        <div className="software-section">{tr('ONLINE — 4', 'ONLINE — 4')}</div>
        {['#ef5252', '#3b82f6', '#10b981', '#8b5cf6'].map((color, i) => (
          <div className="software-person" key={i}>
            <i style={{ background: color }} />
            <div>
              <span className="sk-bar" />
              <span className="sk-bar-sub" />
            </div>
          </div>
        ))}
      </div>
      <div className="software-main">
        <div className="software-channel">
          <Hash size={15} />
          <b>general</b>
        </div>
        <div className="software-messages-list">
          <div className="software-msg-card">
            <i style={{ background: '#ef5252' }}>M</i>
            <div>
              <span className="sk-msg-head" />
              <span className="sk-msg-body" />
              <span className="sk-msg-body-2" />
            </div>
          </div>
          <div className="software-msg-card">
            <i style={{ background: '#3b82f6' }}>A</i>
            <div>
              <span className="sk-msg-head" />
              <span className="sk-msg-body" />
            </div>
          </div>
          <div className="software-msg-card">
            <i style={{ background: '#10b981' }}>E</i>
            <div>
              <span className="sk-msg-head" />
              <span className="sk-msg-body" />
              <span className="sk-msg-body-2" />
            </div>
          </div>
        </div>
        <div className="software-compose">
          <Plus size={16} />
          <span>{tr('Message #general...', 'Nachricht an #general...')}</span>
          <Send size={14} style={{ marginLeft: 'auto', opacity: 0.5 }} />
        </div>
      </div>
    </div>
  );

  return (
    <div className={'auth-minimal' + (mode === 'home' ? ' is-home' : '')} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
      <header className="auth-topbar">
        <a
          className="auth-mark"
          href="/"
          aria-label="MALVO Chat"
          onClick={e => {
            e.preventDefault();
            change('home');
          }}
        >
          <img src="/icon.png" alt="MALVO" />
        </a>
        <button
          type="button"
          className={'auth-home ' + (mode === 'home' ? 'active' : '')}
          onClick={() => change(mode === 'home' ? 'login' : 'home')}
        >
          <Home size={13} />
          {tr('Home', 'Startseite')}
        </button>
        <button
          type="button"
          className="auth-top-login"
          onClick={() => change(mode === 'login' ? 'register' : 'login')}
        >
          <LogIn size={13} />
          {mode === 'login' ? tr('Sign Up', 'Registrieren') : tr('Log In', 'Anmelden')}
        </button>
      </header>

      {mode === 'home' ? (
        <HomeLanding tr={tr} onStart={() => change('login')} onRegister={() => change('register')} />
      ) : (
        <main className="auth-stage">
          <section key={mode} className="auth-fields">
            <h1>
              {mode==='2fa'?tr('Verify your sign-in','Anmeldung bestätigen'):mode === 'register'
                ? tr('Create Account', 'Konto erstellen')
                : mode === 'forgot'
                ? tr('Forgot Password', 'Passwort vergessen')
                : mode === 'reset-code'
                ? tr('Reset Code & Password', 'Code & Neues Passwort')
                : mode === 'reset'
                ? tr('New Password', 'Neues Passwort')
                : tr('Log In', 'Anmelden')}
            </h1>
            <form onSubmit={submit}>
              <fieldset disabled={busy}>
                {mode === 'register' && (
                  <div className="auth-field">
                    <label htmlFor="auth-username">
                      <AtSign size={13} />
                      {tr('Username', 'Nutzername')}
                    </label>
                    <input
                      id="auth-username"
                      name="username"
                      placeholder={tr('yourname', 'deinname')}
                      pattern="[a-zA-Z0-9_]{3,24}"
                      title={tr(
                        '3 to 24 characters: letters, numbers and underscore',
                        '3 bis 24 Zeichen: Buchstaben, Zahlen und Unterstrich'
                      )}
                      minLength={3}
                      maxLength={24}
                      autoComplete="username"
                      required
                    />
                  </div>
                )}
                {(mode === 'login' || mode === 'register' || mode === 'forgot' || (mode === 'reset-code' && !resetEmail)) && (
                  <div className="auth-field">
                    <label htmlFor="auth-email">
                      <Mail size={13} />
                      {tr('Email', 'E-Mail')}
                    </label>
                    <input
                      id="auth-email"
                      name="email"
                      type="email"
                      defaultValue={resetEmail}
                      placeholder="your@email.com"
                      autoComplete="email"
                      required
                    />
                  </div>
                )}
                {mode === 'reset-code' && (
                  <div className="auth-field">
                    <label>
                      <KeyRound size={13} />
                      {tr('6-Digit Verification Code', '6-stelliger Sicherheitscode')}
                    </label>
                    <SixDigitCodeInput value={resetCode} onChange={setResetCode} disabled={busy} autoFocus />
                  </div>
                )}
                {mode==='2fa'&&<div className="auth-field"><p>{useRecovery?tr('Enter one unused recovery code. It becomes invalid after this sign-in.','Gib einen unbenutzten Wiederherstellungscode ein. Nach dieser Anmeldung ist er ungültig.'):tr('Enter the current six-digit code from your authenticator app.','Gib den aktuellen sechsstelligen Code aus deiner Authenticator-App ein.')}</p><label htmlFor="auth-factor-code"><Shield size={13}/>{useRecovery?tr('Recovery code','Wiederherstellungscode'):tr('Authenticator code','Authenticator-Code')}</label><input id="auth-factor-code" name="code" value={factorCode} onChange={e=>setFactorCode(e.target.value)} inputMode={useRecovery?'text':'numeric'} autoComplete="one-time-code" maxLength={useRecovery?80:6} pattern={useRecovery?undefined:'[0-9]{6}'} autoFocus required/><button type="button" className="auth-factor-alternative" onClick={()=>{setUseRecovery(!useRecovery);setFactorCode('');setError('')}}>{useRecovery?tr('Use authenticator app','Authenticator-App verwenden'):tr('Use a recovery code','Wiederherstellungscode verwenden')}</button></div>}
                {mode !== 'forgot' && mode!=='2fa' && (
                  <div className="auth-field">
                    <div className="auth-label-row">
                      <label htmlFor="auth-password">
                        <Lock size={13} />
                        {mode.startsWith('reset') ? tr('New Password', 'Neues Passwort') : tr('Password', 'Passwort')}
                      </label>
                      {mode === 'login' && (
                        <button type="button" onClick={() => change('forgot')}>
                          {tr('Forgot Password?', 'Passwort vergessen?')}
                        </button>
                      )}
                    </div>
                    <input
                      id="auth-password"
                      name="password"
                      type="password"
                      placeholder={mode === 'login' ? tr('Enter your password', 'Dein Passwort') : tr('At least 12 characters', 'Mindestens 12 Zeichen')}
                      minLength={mode === 'login' ? 1 : 12}
                      maxLength={128}
                      autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                      required
                    />
                  </div>
                )}
                {mode === 'register' && <input className="auth-hp" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" />}
                {mode === 'register' && captchaSiteKey && <Captcha siteKey={captchaSiteKey} />}
                {mode === 'register' && <p className="auth-terms-note" style={{fontSize:12,color:'#9696a4',margin:'4px 0 8px',lineHeight:1.5}}>{tr('By creating an account you agree to the ','Mit der Registrierung stimmst du den ')}<a href={tr('/tos','/tos?lang=de')} target="_blank" rel="noopener noreferrer">{tr('Terms of Service','Nutzungsbedingungen')}</a>{tr(' and confirm you are at least 13. See the ',' zu und bestätigst, dass du mindestens 13 Jahre alt bist. Siehe ')}<a href={tr('/privacy','/privacy?lang=de')} target="_blank" rel="noopener noreferrer">{tr('Privacy Policy','Datenschutzerklärung')}</a>.</p>}
                <button className="auth-enter" disabled={busy}>
                  <LogIn size={15} />
                  {busy
                    ? tr('One moment…', 'Einen Moment …')
                    : mode === 'register'
                    ? tr('Create Account', 'Registrieren')
                    : mode==='2fa'?tr('Verify and sign in','Bestätigen und anmelden')
                    : mode === 'forgot'
                    ? tr('Send Reset Code', 'Code anfordern')
                    : mode.startsWith('reset')
                    ? tr('Save Password', 'Passwort speichern')
                    : tr('Log In', 'Anmelden')}
                </button>
              </fieldset>
            </form>
            {error && (
              <p className="auth-feedback error" role="alert">
                {error}
              </p>
            )}
            {note && (
              <p className="auth-feedback success" role="status">
                {note}
              </p>
            )}
            {mode === 'forgot' && !mailEnabled && (
              <p className="auth-feedback">
                {tr('Email delivery is temporarily unavailable.', 'Der E-Mail-Versand ist vorübergehend nicht verfügbar.')}
              </p>
            )}
            {mode==='register'&&<p className="auth-registration-info">{tr('To prevent abuse, we record your registration IP. Only the platform owner can view it.','Zur Missbrauchsprävention erfassen wir deine Registrierungs-IP. Nur der Plattformbetreiber kann sie einsehen.')}</p>}
            {mode === 'login' && <QrLogin request={request} onDone={onDone} />}
            <div className="auth-bottom-link">
              {mode === 'login' ? (
                <>
                  {tr("Don't have an account?", 'Noch kein Konto?')}{' '}
                  <button type="button" onClick={() => change('register')}>
                    {tr('Sign up', 'Registrieren')}
                  </button>
                </>
              ) : mode === 'register' ? (
                <>
                  {tr('Already have an account?', 'Bereits registriert?')}{' '}
                  <button type="button" onClick={() => change('login')}>
                    {tr('Log In', 'Anmelden')}
                  </button>
                </>
              ) : mode === 'reset-code' ? (
                <button type="button" onClick={() => change('forgot')}>
                  <ArrowLeft size={12} />
                  {tr('Request another code', 'Anderen Code anfordern')}
                </button>
              ) : (
                <button type="button" onClick={() => change('login')}>
                  <ArrowLeft size={12} />
                  {tr('Back to Log In', 'Zur Anmeldung')}
                </button>
              )}
            </div>
          </section>
          <div className="software-scene" aria-label="MALVO Chat Preview">
            {renderPreviewWindow()}
          </div>
        </main>
      )}

      <footer className="auth-clean-footer">
        <div>
          <a href={tr('/privacy','/privacy?lang=de')}>
            <Shield size={12} />
            {tr('Privacy', 'Datenschutz')}
          </a>
          <a href={tr('/tos','/tos?lang=de')}>{tr('Terms', 'Nutzungsbedingungen')}</a>
          <a href={tr('/legal','/legal?lang=de')}>{tr('Legal Notice', 'Impressum')}</a>
          <button type="button" onClick={() => setInfo('about')}>
            {tr('About MALVO', 'Über MALVO')}
          </button>
        </div>
        <span>© {new Date().getFullYear()} MALVO Chat</span>
      </footer>

      {info && (
        <div className="modal-backdrop">
          <section
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-label={info === 'privacy' ? tr('Privacy', 'Datenschutz') : tr('About MALVO', 'Über MALVO')}
          >
            <button className="close-modal" title={tr('Close', 'Schließen')} onClick={() => setInfo(null)}>
              <X size={19} />
            </button>
            <h2>{info === 'privacy' ? tr('Privacy Policy', 'Datenschutz') : 'MALVO Chat'}</h2>
            <p>
              {info === 'privacy'
                ? tr(
                    'MALVO stores account data, messages, and uploaded files securely on our dedicated servers. Session cookies are used only for authentication.',
                    'Informationen zur Datenverarbeitung findest du in der Datenschutzerklärung unter malvo.eu/datenschutz.html.'
                  )
                : tr(
                    'An independent next-gen community & voice chat platform with native Windows app. Not affiliated with Discord.',
                    'Eine eigenständige Next-Gen Chat-App für Communities, Freunde und Sprachräume. Nicht mit Discord verbunden.'
                  )}
            </p>
            <button className="secondary" onClick={() => setInfo(null)}>
              {tr('Close', 'Schließen')}
            </button>
          </section>
        </div>
      )}
    </div>
  );
}
